import {
  APICallState,
  MFAActions,
  MFAChannel,
  MFAEmail,
  PreferenceSelectorState,
  Selectors,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {useCallback, useEffect, useState} from 'react';
import {ROUTES} from 'routes';

import ErrorPanel from 'components/ErrorPanel';
import PreferenceSelectorWrapper from 'components/PreferenceSelectorWrapper';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

const ConnectedPreferenceSelector: React.FunctionComponent = () => {
  const dispatch = useWidgetDispatch();
  const navigate = useNavigateWithParams();
  const {trackAdobeEvent} = useTrackEvent();
  const securityEmail = useWidgetState(Selectors.securityEmailSelector) as
    | string
    | undefined;
  const securityPhone = useWidgetState(
    Selectors.securityMobilePhoneSelector,
  ) as string | undefined;
  const loadStatus = useWidgetState(Selectors.mfaProfileStatusSelector);
  const selectedChannel = useWidgetState(Selectors.selectedChannelSelector) as
    | MFAChannel
    | undefined;
  const editProfileState = useWidgetState(Selectors.editProfileStateSelector);
  const preferenceSelectorState = useWidgetState(
    Selectors.preferenceSelectorStateSelector,
  ) as PreferenceSelectorState;
  const [retryInProgress, setRetryInProgress] = useState('none');
  const isVip = useWidgetState(Selectors.isVIPSelector) as boolean;

  const mfaMarketingEmail = useWidgetState(
    Selectors.mfaMarketingEmailSelector,
  ) as MFAEmail | undefined;

  const onNextPress = useCallback(async () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: `mfa opt in start`,
      },
    });

    if (editProfileState === APICallState.ERROR) {
      setRetryInProgress('confirmation');
    }

    switch (preferenceSelectorState) {
      case PreferenceSelectorState.AddMarketingEmail:
        return dispatch(
          // Do not pass in marketing email in payload for security reasons
          // Backend will find the account's marketing email and use that value instead
          MFAActions.editSecurityEmail({
            securityEmail: '',
          }),
        );
      case PreferenceSelectorState.SelectVerifiedEmail:
        return dispatch(
          MFAActions.changeChannelPreference({
            channelPreference: MFAChannel.EMAIL,
            setOptin: true,
          }),
        );
      case PreferenceSelectorState.SelectVerifiedPhone:
        return dispatch(
          MFAActions.changeChannelPreference({
            channelPreference: MFAChannel.SMS,
            setOptin: true,
          }),
        );
      default:
        return null;
    }
  }, [editProfileState, preferenceSelectorState, dispatch, trackAdobeEvent]);

  const onSelectChannel = useCallback(
    (channel: MFAChannel | undefined) => {
      if (!channel) {
        dispatch(
          MFAActions.setPreferenceSelectorState(
            PreferenceSelectorState.Initial,
          ),
        );
        dispatch(MFAActions.setChannel(undefined));
        return;
      }

      if (channel === selectedChannel) {
        return;
      }

      let state: PreferenceSelectorState;
      switch (channel) {
        case MFAChannel.EMAIL:
          if (securityEmail) {
            state = PreferenceSelectorState.SelectVerifiedEmail;
            break;
          }
          state = PreferenceSelectorState.AddMarketingEmail;
          break;
        case MFAChannel.SMS:
          state = PreferenceSelectorState.SelectVerifiedPhone;
          break;
        default:
          state = PreferenceSelectorState.Initial;
          break;
      }
      dispatch(MFAActions.setPreferenceSelectorState(state));

      dispatch(MFAActions.setChannel(channel));
    },
    [selectedChannel, dispatch, securityEmail],
  );

  // Reset state of retry in progress
  useEffect(() => {
    if (loadStatus === APICallState.ERROR && retryInProgress === 'profile') {
      setRetryInProgress('none');
    }
  }, [loadStatus, retryInProgress]);

  useEffect(() => {
    if (
      editProfileState === APICallState.ERROR &&
      retryInProgress === 'confirmation'
    ) {
      setRetryInProgress('none');
    }
  }, [retryInProgress, editProfileState]);

  useEffect(() => {
    if (selectedChannel === undefined) {
      if (securityPhone) {
        onSelectChannel(MFAChannel.SMS);
      } else if (!!securityEmail || !!mfaMarketingEmail?.value) {
        onSelectChannel(MFAChannel.EMAIL);
      }
    }
  }, [
    onSelectChannel,
    selectedChannel,
    mfaMarketingEmail?.value,
    securityEmail,
    securityPhone,
  ]);

  const onRetryGetSecurityProfilePress = useCallback(() => {
    if (loadStatus === APICallState.ERROR) {
      setRetryInProgress('profile');
    }

    dispatch(MFAActions.getSecurityProfile());
  }, [dispatch, loadStatus]);

  if (editProfileState === APICallState.SUCCESS) {
    switch (preferenceSelectorState) {
      case PreferenceSelectorState.AddMarketingEmail:
        navigate(ROUTES.stepUp, {
          state: {
            contactInfoEmail: mfaMarketingEmail?.value,
            isEnrolling: true,
          },
        });
        break;
      case PreferenceSelectorState.SelectVerifiedEmail:
      case PreferenceSelectorState.SelectVerifiedPhone:
        navigate(ROUTES.stepUp, {state: {isEnrolling: true}});
        break;
      default:
        break;
    }
    return null;
  }

  const getErrorPanel = (onRetryPress: any, onCancelPress: any) => {
    return (
      <ErrorPanel
        retryInProgress={retryInProgress !== 'none'}
        onRetryPress={onRetryPress}
        onCancelPress={onCancelPress}
      />
    );
  };

  if (
    editProfileState === APICallState.ERROR ||
    retryInProgress === 'confirmation'
  ) {
    return getErrorPanel(onNextPress, undefined);
  }

  if (loadStatus === APICallState.ERROR || retryInProgress === 'profile') {
    return getErrorPanel(onRetryGetSecurityProfilePress, undefined);
  }

  return (
    <PreferenceSelectorWrapper
      onSendPress={onNextPress}
      securityEmail={securityEmail}
      mobilePhone={securityPhone}
      onSelectChannel={onSelectChannel}
      selectedChannel={selectedChannel}
      isVip={isVip}
      isLoading={[loadStatus, editProfileState].includes(APICallState.LOADING)}
      marketingEmail={mfaMarketingEmail?.value}
    />
  );
};

export default ConnectedPreferenceSelector;
